import '../../ForBrands/brandMode.css';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ReactPaginate from 'react-paginate';
import { useReactTable, getCoreRowModel, flexRender } from '@tanstack/react-table';
import { fetchUsersByRole, clearUsersByRole } from '../../../store/actions/userActions';
import { calculateProfileCompletion, calculateStatisticsCompletion, extractPositionAbbreviation } from '../../../utils/utils';
import { Avatar, Badge, BmModal, Btn, Card, Checkbox, EmptyState, Loading, SearchBox, Select } from '../../ForBrands/ui/BrandUI';
import { CompletionPair, pctDot, pctTone } from './completion';
import { RowCheckbox, USERS_PAGINATE_CLASSES } from './usersTable';

const PAGE_SIZES = [5, 10, 25, 50, 100, 200, 500, 1000];

// Users table cell widths, trimmed to this dialog's columns.
const cellWidth = (id) => (
  id === 'select' ? 'w-[32px]' :
  id === 'rowNumber' ? 'w-[36px]' :
  id === 'name' ? 'max-w-[220px]' :
  id === 'completion' ? 'w-[110px]' :
  id === 'cacheScore' ? 'w-[84px]' :
  'max-w-[160px]'
);

const AssignDialog = ({ open, intern, onClose, onAssign, previousSelection, setPreviousSelection }) => {
  const dispatch = useDispatch();
  const allUsers = useSelector((state) => state.user?.usersByRole || []);
  const totalCounts = useSelector((state) => state.user?.usersByRoleTotalCounts || 0);
  const codes = useSelector((state) => state.codes?.allCodes || []);
  const [rowSelection, setRowSelection] = useState(previousSelection || {});
  const [rowData, setRowData] = useState([]);

  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (open) {
      setLoading(true);
      Promise.all([
        dispatch(clearUsersByRole()),
        dispatch(fetchUsersByRole({ page, limit: itemsPerPage, search: searchQuery })),
      ]).finally(() => setLoading(false));
    }
  }, [dispatch, open, itemsPerPage, page, searchQuery]);

  useEffect(() => {
    if (allUsers && intern) {
      const allAthletes = allUsers.map(user => user);
      setRowData(allAthletes);

      // Pre-select athletes already assigned to this intern
      const assignedRows = { ...previousSelection };
      allAthletes.forEach(user => {
        if (intern.options?.assignedAthleteEmails?.includes(user.email)) {
          assignedRows[user.email] = true;
        }
      });
      setRowSelection(assignedRows);
    }
  }, [intern, allUsers, previousSelection]);

  const codeName = (type, value) => codes.find((c) => c.type === type && c.value === value)?.name || '';
  const positionsOf = (row) => {
    const primarySport = row.athlete?.primarySport;
    if (!primarySport) return '';
    const positions = codes.find((c) => c.type === 'sport' && c.value === primarySport)?.positions || [];
    const values = Array.isArray(row.athlete?.position)
      ? row.athlete.position
      : row.athlete?.position != null ? [row.athlete.position] : [];
    return values
      .map((val) => extractPositionAbbreviation(positions.find((pos) => pos.value === val)?.name || ''))
      .filter(Boolean)
      .join(', ');
  };
  const dash = <span className="text-bm-tertiary">-</span>;

  const columns = useMemo(() => [
    {
      id: 'select',
      header: () => (
        <RowCheckbox
          checked={table.getIsAllPageRowsSelected()}
          indeterminate={table.getIsSomePageRowsSelected()}
          onChange={(next) => table.toggleAllPageRowsSelected(next)}
          label="Select every player on this page"
        />
      ),
      cell: ({ row }) => (
        <RowCheckbox
          checked={row.getIsSelected()}
          onChange={(next) => row.toggleSelected(next)}
          label={`Select ${row.original.name || row.original.email}`}
        />
      ),
    },
    {
      id: 'rowNumber', // static ID (no accessor)
      header: 'No',
      cell: () => null, // placeholder, the row renders the number
    },
    {
      id: 'name',
      header: 'Player',
      accessorKey: 'name',
      cell: ({ row }) => {
        const u = row.original;
        const avatarUrl = u.athlete?.avatar?.replace('/upload/', '/upload/w_48,h_48,c_fill,g_face,q_auto/');
        return (
          <div className="flex min-w-0 items-center gap-3">
            <Avatar name={u.name || u.email} src={avatarUrl || ''} size={32} />
            <span className="truncate bm-label-l text-bm-ink">{u.name || u.email}</span>
          </div>
        );
      },
    },
    {
      id: 'primarySport',
      header: 'Sport',
      accessorFn: (row) => codeName('sport', row.athlete?.primarySport),
      cell: ({ row, getValue }) => {
        const sport = getValue();
        const pos = positionsOf(row.original);
        if (!sport) return dash;
        return <span>{sport}{pos ? <span className="text-bm-muted"> · {pos}</span> : null}</span>;
      },
    },
    {
      id: 'currentTeam',
      header: 'Team',
      accessorFn: (row) => codeName('team', row.athlete?.currentTeam),
      cell: ({ row, getValue }) => {
        const team = getValue();
        const school = codeName('school', row.original.athlete?.previousSchool);
        return (
          <div className="min-w-0">
            <div className="truncate">{team || dash}</div>
            {school && school !== team && (
              <div className="truncate bm-body-s text-bm-muted" title="Educational history">{school}</div>
            )}
          </div>
        );
      },
    },
    {
      id: 'completion',
      header: 'Completion',
      accessorFn: (row) => calculateProfileCompletion(row.athlete || {}),
      cell: ({ row, getValue }) => {
        if (!(row.original.permissions || []).includes('IS_ATHLETE')) return null;
        return <CompletionPair profile={getValue()} stats={calculateStatisticsCompletion(row.original)} />;
      },
    },
    {
      id: 'cacheScore',
      header: 'CacheScore',
      accessorKey: 'athlete.statistics.final_score',
      cell: ({ row }) => {
        if (!(row.original.permissions || []).includes('IS_ATHLETE')) return null;
        const v = row.original.athlete?.statistics?.final_score;
        return v ? <span className="bm-label-l text-bm-ink">{v}</span> : dash;
      },
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [codes]);

  const table = useReactTable({
    data: rowData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.email,
    state: {
      rowSelection,
    },
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
  });

  const handleConfirm = () => {
    const totalEmails = rowData.map(row => row.email);
    const checkedEmails = table.getSelectedRowModel().rows.map(row => row.original.email);

    setPreviousSelection(rowSelection); // persist selection
    onAssign(totalEmails, checkedEmails);
    onClose();
  };

  if (!open) return null;

  const pageCount = Math.ceil(totalCounts / itemsPerPage);
  const selectedCount = table.getSelectedRowModel().rows.length;
  const firstShown = Math.min((page - 1) * itemsPerPage + 1, totalCounts);
  const lastShown = Math.min(page * itemsPerPage, totalCounts);
  const applySearch = () => setSearchQuery(inputValue);
  const visibleAthletes = table.getRowModel().rows.map((row) => row.original);
  const allVisibleSelected = visibleAthletes.length > 0 && visibleAthletes.every((u) => rowSelection[u.email]);
  const someVisibleSelected = !allVisibleSelected && visibleAthletes.some((u) => rowSelection[u.email]);

  return (
    <BmModal
      title={`Assign players to ${intern?.name || intern?.email || 'intern'} (Intern)`}
      onClose={onClose}
      width={1100}
      footer={(
        <>
          <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn onClick={handleConfirm} disabled={rowData.length === 0}>
            {`Assign ${selectedCount} ${selectedCount === 1 ? 'player' : 'players'}`}
          </Btn>
        </>
      )}
    >
      {/* Toolbar: search, pagination, page size */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-[1_1_240px] items-center gap-2">
          <SearchBox
            value={inputValue}
            onChange={setInputValue}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applySearch(); } }}
            placeholder="Search by name, sport or current team, then press Enter"
            aria-label="Search players"
            className="flex-1"
          />
          <Btn variant="secondary" onClick={applySearch}>Search</Btn>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {pageCount > 1 && (
            <ReactPaginate
              previousLabel="← Prev"
              nextLabel="Next →"
              breakLabel="..."
              pageCount={pageCount}
              marginPagesDisplayed={1}
              pageRangeDisplayed={3}
              onPageChange={({ selected }) => setPage(selected + 1)}
              {...USERS_PAGINATE_CLASSES}
              forcePage={Math.min(page - 1, pageCount - 1)}
            />
          )}
          <label className="flex items-center gap-2 bm-body-s text-bm-muted">
            Rows
            <Select
              value={itemsPerPage}
              onChange={(e) => { setItemsPerPage(parseInt(e.target.value, 10)); setPage(1); }}
              className="!h-8 !w-auto !py-0 bm-label-xs"
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </Select>
          </label>
        </div>
      </div>

      <p className="mt-3 bm-body-s text-bm-muted">
        {totalCounts > 0 ? `Showing ${firstShown} to ${lastShown} of ${totalCounts} players` : 'No players'}
      </p>

      <div className="mt-3">
        {loading ? (
          <Loading label="Loading players..." />
        ) : rowData.length === 0 ? (
          <EmptyState>No players match this search.</EmptyState>
        ) : (
          <>
            {/* Table (sm and up) */}
            <Card padding="p-0" className="hidden w-full min-w-0 overflow-hidden sm:block">
              <div className="max-h-[50vh] w-full overflow-auto">
                <table className="w-full text-left">
                  <thead className="sticky top-0 z-10 bg-bm-subtle">
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <th
                            key={header.id}
                            className={`whitespace-nowrap border-b border-bm-border px-2.5 py-2.5 bm-label-xs uppercase tracking-[0.06em] text-bm-muted ${cellWidth(header.column.id)}`}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {table.getRowModel().rows.map((row, i) => (
                      <tr
                        key={row.id}
                        className={`border-b border-bm-border transition-colors hover:bg-bm-subtle ${row.getIsSelected() ? 'bg-bm-subtle' : 'bg-white'}`}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            className={`overflow-hidden text-ellipsis whitespace-nowrap px-2.5 py-2.5 bm-body-m text-bm-ink ${cellWidth(cell.column.id)}`}
                          >
                            {cell.column.id === 'rowNumber'
                              ? <span className="text-bm-tertiary">{(page - 1) * itemsPerPage + i + 1}</span>
                              : flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Cards (phones) */}
            <div className="flex flex-col gap-3 sm:hidden">
              <Checkbox
                checked={allVisibleSelected}
                indeterminate={someVisibleSelected}
                onChange={(next) => {
                  setRowSelection((prev) => {
                    const updated = { ...prev };
                    visibleAthletes.forEach((user) => { updated[user.email] = next; });
                    return updated;
                  });
                }}
                label="Select all on this page"
                className="px-1"
              />
              <div className="flex max-h-[50vh] flex-col gap-3 overflow-y-auto">
                {rowData.map((user) => {
                  const profilePct = calculateProfileCompletion(user.athlete || {});
                  const statsPct = calculateStatisticsCompletion(user);
                  const pos = positionsOf(user);
                  return (
                    <Card key={user.email} padding="p-4" className="flex items-start gap-3">
                      <RowCheckbox
                        checked={!!rowSelection[user.email]}
                        onChange={(next) => setRowSelection((prev) => ({ ...prev, [user.email]: next }))}
                        label={`Select ${user.name || user.email}`}
                      />
                      <Avatar name={user.name || user.email} src={user.athlete?.avatar || ''} size={44} />
                      <div className="min-w-0 flex-1">
                        <div className="truncate bm-label-l text-bm-ink">{user.name || user.email}</div>
                        <div className="mt-1 flex flex-col gap-[2px] bm-body-s text-bm-muted">
                          <span>Sport: {codeName('sport', user.athlete?.primarySport) || '-'}{pos ? ` · ${pos}` : ''}</span>
                          <span>Team: {codeName('team', user.athlete?.currentTeam) || '-'}</span>
                          <span>
                            {user.athlete?.levelOfPlay === 1 ? 'School' : user.athlete?.levelOfPlay === 2 ? 'College/University' : 'Previous school'}:{' '}
                            {codeName('school', user.athlete?.previousSchool) || '-'}
                          </span>
                          <span>CacheScore: {user.athlete?.statistics?.final_score || '-'}</span>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          <Badge tone={pctTone(profilePct)} dot={pctDot(profilePct)}>Profile {profilePct}%</Badge>
                          <Badge tone={pctTone(statsPct)} dot={pctDot(statsPct)}>Stats {statsPct}%</Badge>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </BmModal>
  );
};

export default AssignDialog;
